import { useMemo } from "react";
import { useTheme } from "@/lib/theme";

interface Point {
  t: number; // timestamp ms
  p: number;
}

interface Props {
  data: Point[];
  height?: number;
  className?: string;
  currency?: string;
  fmtMoney?: (v: number, c?: string) => string;
  fmtDate?: (v: string | Date | number | null | undefined) => string;
}

/**
 * Chart SVG simple : line chart avec aire dégradée + axes (4 ticks Y, 4 ticks X).
 * Pas de lib externe. Tooltip basique sur survol via title.
 */
export function PriceChart({ data, height = 220, className, currency = "EUR", fmtMoney, fmtDate }: Props) {
  const { theme } = useTheme();

  const layout = useMemo(() => {
    const padL = 44;
    const padR = 8;
    const padT = 8;
    const padB = 24;
    const W = 800;
    const H = height;
    const innerW = W - padL - padR;
    const innerH = H - padT - padB;

    if (data.length === 0) return { W, H, padL, padR, padT, padB, innerW, innerH, sorted: [] as Point[], xMin: 0, xMax: 0, yMin: 0, yMax: 0 };
    const sorted = data.slice().sort((a, b) => a.t - b.t);
    const xMin = sorted[0].t;
    const xMax = sorted[sorted.length - 1].t;
    let yMin = sorted.reduce((m, p) => Math.min(m, p.p), Infinity);
    let yMax = sorted.reduce((m, p) => Math.max(m, p.p), -Infinity);
    if (!Number.isFinite(yMin) || !Number.isFinite(yMax)) { yMin = 0; yMax = 1; }
    if (yMin === yMax) { yMin = Math.max(0, yMin * 0.95); yMax = yMax * 1.05 || 1; }
    const yRange = yMax - yMin || 1;
    yMin = yMin - yRange * 0.05;
    yMax = yMax + yRange * 0.05;

    return { W, H, padL, padR, padT, padB, innerW, innerH, sorted, xMin, xMax, yMin, yMax };
  }, [data, height]);

  if (data.length === 0) {
    return (
      <div
        className={className}
        style={{ height }}
        role="img"
        aria-label="No price data"
      >
        <div className="flex h-full items-center justify-center text-xs text-muted-fg">No price observations yet.</div>
      </div>
    );
  }

  const { W, H, padL, padR, padT, innerW, innerH, sorted, xMin, xMax, yMin, yMax } = layout;
  const xRange = (xMax - xMin) || 1;
  const yRange = (yMax - yMin) || 1;
  const xOf = (t: number) => padL + ((t - xMin) / xRange) * innerW;
  const yOf = (p: number) => padT + innerH - ((p - yMin) / yRange) * innerH;

  // Line path
  const linePath = sorted.map((p, i) => `${i === 0 ? "M" : "L"}${xOf(p.t).toFixed(1)},${yOf(p.p).toFixed(1)}`).join(" ");
  // Area path (line then to baseline)
  const areaPath = `${linePath} L${xOf(sorted[sorted.length - 1].t).toFixed(1)},${(padT + innerH).toFixed(1)} L${xOf(sorted[0].t).toFixed(1)},${(padT + innerH).toFixed(1)} Z`;

  // Y ticks (4 nice values)
  const yTicks = [0, 1, 2, 3].map((i) => yMin + (yRange * i) / 3);
  // X ticks (5 evenly spaced timestamps)
  const xTicks = [0, 1, 2, 3, 4].map((i) => xMin + (xRange * i) / 4);

  const stroke = theme === "dark" ? "rgba(125,158,255,0.95)" : "rgba(46,82,210,0.95)";
  const fillTop = theme === "dark" ? "rgba(125,158,255,0.32)" : "rgba(46,82,210,0.18)";
  const fillBot = theme === "dark" ? "rgba(125,158,255,0)" : "rgba(46,82,210,0)";
  const axisColor = theme === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)";
  const labelColor = theme === "dark" ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.5)";

  const fmt = fmtMoney
    ? (v: number) => fmtMoney(v, currency)
    : (v: number) => v.toFixed(2) + (currency === "EUR" ? " €" : ` ${currency}`);
  const fmtX = fmtDate
    ? (t: number) => fmtDate(new Date(t)).slice(0, 6)
    : (t: number) => new Date(t).toISOString().slice(0, 10);

  return (
    <svg
      role="img"
      aria-label="Price over time"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      className={className}
      style={{ width: "100%", height }}
    >
      <defs>
        <linearGradient id="priceFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={fillTop} />
          <stop offset="100%" stopColor={fillBot} />
        </linearGradient>
      </defs>

      {/* horizontal grid */}
      {yTicks.map((v, i) => (
        <g key={i}>
          <line
            x1={padL}
            x2={W - padR}
            y1={yOf(v)}
            y2={yOf(v)}
            stroke={axisColor}
            strokeWidth={1}
          />
          <text x={padL - 6} y={yOf(v) + 3} fontSize={10} textAnchor="end" fill={labelColor}>
            {fmt(v)}
          </text>
        </g>
      ))}

      {/* x labels */}
      {xTicks.map((tx, i) => (
        <text
          key={i}
          x={xOf(tx)}
          y={H - 6}
          fontSize={10}
          textAnchor={i === 0 ? "start" : i === xTicks.length - 1 ? "end" : "middle"}
          fill={labelColor}
        >
          {fmtX(tx)}
        </text>
      ))}

      {/* area */}
      <path d={areaPath} fill="url(#priceFill)" />
      {/* line */}
      <path d={linePath} fill="none" stroke={stroke} strokeWidth={1.8} strokeLinejoin="round" strokeLinecap="round" />

      {/* points */}
      {sorted.length <= 80 ? sorted.map((p, i) => (
        <circle
          key={i}
          cx={xOf(p.t)}
          cy={yOf(p.p)}
          r={2.2}
          fill={stroke}
        >
          <title>{`${new Date(p.t).toLocaleDateString()} — ${fmt(p.p)}`}</title>
        </circle>
      )) : null}
    </svg>
  );
}
